import type {
  CreateSimulationIssueInput,
  DirectorEngine,
  DirectorDecisionItem,
  SimulationDetail,
  SimulationIssueSeverity,
  SimulationIssueType,
  SimulationRunListItem,
  SimulationStatus,
  StoryDetail,
  StoryListItem,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

const STATUS_LABELS: Record<SimulationStatus, string> = {
  PAUSED: "现场已暂停",
  CREATED: "已创建",
  RUNNING: "正在生成回应",
  WAITING_FOR_USER: "等待用户",
  COMPLETED: "已完成",
  STOPPED: "已停止",
  FAILED: "失败",
};

const ISSUE_TYPE_LABELS: Record<SimulationIssueType, string> = {
  CHARACTER_CROSSTALK: "角色串台",
  USER_ACTION_TAKEN: "替用户行动",
  STATE_ERROR: "状态错误",
  PACING_ERROR: "节奏错误",
  DIALOGUE_QUALITY: "对话质量",
  VOICE_MISMATCH: "音色不合",
  SECRET_LEAK: "秘密泄露",
  IMMERSION_BREAK: "沉浸感中断",
  TIME_CONTRADICTION: "时间矛盾",
  LOCATION_CONTRADICTION: "地点矛盾",
  CHARACTER_PRESENCE_ERROR: "人物位置错误",
  KNOWLEDGE_ORDER_ERROR: "知情顺序错误",
  PROP_PROVENANCE_MISSING: "物件来源缺失",
  CAUSAL_GAP: "因果断裂",
  POV_ERROR: "视角错误",
  CANONICAL_TRUTH_MISSING: "唯一真相冲突",
  ENDING_UNSUPPORTED: "结局依据不足",
  OTHER: "其他问题",
};

const ISSUE_SEVERITY_LABELS: Record<SimulationIssueSeverity, string> = {
  ERROR: "错误",
  WARNING: "警告",
  NOTE: "备注",
};

function errorMessage(caught: unknown, fallback: string): string {
  return caught instanceof ApiError ? caught.message : fallback;
}

function Feedback({ error, success }: { error: string; success: string }): React.ReactNode {
  if (error) return <div className="error-notice" role="alert"><span>!</span>{error}</div>;
  if (success) return <div className="save-notice" role="status"><span>✓</span>{success}</div>;
  return null;
}

function statusClass(status: SimulationStatus): string {
  if (status === "COMPLETED") return "completed";
  if (status === "STOPPED" || status === "FAILED") return "stopped";
  return "waiting";
}

function providerLabel(provider: string): string {
  if (provider === "OPENAI_COMPATIBLE") return "真实 AI";
  if (provider === "DETERMINISTIC") return "备用导演";
  return provider;
}

export function simulationCharacterOptions(characters: StoryDetail["characters"]): Array<{
  character: StoryDetail["characters"][number];
  availabilityLabel: string;
}> {
  return [...characters]
    .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, "zh-CN"))
    .map((character) => ({
      character,
      availabilityLabel: character.playable
        ? "可扮演"
        : character.kind === "BACKGROUND"
          ? "不可扮演 · 背景人物"
          : "不可扮演 · AI 演绎",
    }));
}

function RunCard({ run, onOpen }: { run: SimulationRunListItem; onOpen: (id: string) => void }): React.ReactNode {
  return (
    <button type="button" className="simulation-run-card" onClick={() => onOpen(run.id)}>
      <span className={`simulation-status-dot ${statusClass(run.status)}`} />
      <span>
        <strong>{run.storyTitle}</strong>
        <small>{run.playableCharacterName} · R{run.draftRevision} · {run.turnSequence} 轮</small>
      </span>
      <span>
        <em>{STATUS_LABELS[run.status]}</em>
        <small>{formatUpdatedAt(run.startedAt)}</small>
      </span>
    </button>
  );
}

function SimulationSetup({
  stories,
  story,
  selectedStoryId,
  selectedCharacterId,
  runs,
  busy,
  run,
  onStory,
  onCharacter,
  engineVersion,
  onEngineVersion,
  onStart,
  onOpenRun,
  onReset,
  onStop,
  onNew,
}: {
  stories: StoryListItem[];
  story: StoryDetail | null;
  selectedStoryId: string;
  selectedCharacterId: string;
  runs: SimulationRunListItem[];
  busy: boolean;
  run: SimulationDetail | null;
  onStory: (id: string) => void;
  onCharacter: (id: string) => void;
  engineVersion: DirectorEngine;
  onEngineVersion: (value: DirectorEngine) => void;
  onStart: () => void;
  onOpenRun: (id: string) => void;
  onReset: () => void;
  onStop: () => void;
  onNew: () => void;
}): React.ReactNode {
  const characters = simulationCharacterOptions(story?.characters ?? []);
  const playableCount = characters.filter(({ character }) => character.playable).length;
  const terminal = run ? ["COMPLETED", "STOPPED", "FAILED"].includes(run.status) : false;
  return (
    <aside className="simulation-setup" aria-label="规则测试设置">
      <div className="simulation-panel-title"><span>01</span><div><strong>规则测试设置</strong><small>选择草稿与玩家角色</small></div></div>
      <label>
        <span>故事线草稿</span>
        <select value={selectedStoryId} onChange={(event) => onStory(event.target.value)} disabled={busy || Boolean(run)}>
          <option value="">选择故事线</option>
          {stories.filter((item) => item.status !== "ARCHIVED").map((item) => <option value={item.id} key={item.id}>{item.title} · R{item.draftRevision}</option>)}
        </select>
      </label>
      {story ? (
        <div className="simulation-story-readiness">
          <span><strong>{story.completion.score}%</strong>结构完成度</span>
          <span className={story.completion.errorCount ? "has-errors" : "ready"}>{story.completion.errorCount ? `${story.completion.errorCount} 个必须解决的问题` : "可以开始测试"}</span>
        </div>
      ) : null}
      <div className="simulation-role-field">
        <span><strong>故事角色</strong>{story ? <small>{characters.length} 位 · {playableCount} 位可扮演</small> : null}</span>
        <div className="simulation-role-list">
          {characters.map(({ character, availabilityLabel }) => {
            const content = <>
              <span className="simulation-role-avatar">{character.name.slice(0, 1)}</span>
              <span className="simulation-role-copy">
                <span><strong>{character.name}</strong><em className={character.playable ? "playable" : "unplayable"}>{availabilityLabel}</em></span>
                <small>{character.publicDescription || (character.playable ? "可扮演角色" : "由 AI 导演安排")}</small>
              </span>
            </>;
            return character.playable ? (
              <button type="button" key={character.id} className={`simulation-role-card${selectedCharacterId === character.id ? " active" : ""}`} onClick={() => onCharacter(character.id)} disabled={busy || Boolean(run)} aria-label={`扮演${character.name}`}>
                {content}
              </button>
            ) : (
              <article key={character.id} className="simulation-role-card unavailable" aria-label={`${character.name}，${availabilityLabel}`}>
                {content}
              </article>
            );
          })}
          {story && !characters.length ? <p className="simulation-inline-empty">这条故事线还没有角色。</p> : null}
          {story && characters.length > 0 && !playableCount ? <p className="simulation-inline-empty">这条故事线还没有可扮演角色。</p> : null}
        </div>
      </div>
      <div className="simulation-engine-card">
        <span className="engine-pulse" />
        <div><strong>故事规则测试</strong><small>使用当前设置的故事导演</small></div>
      </div>
      <label>
        <span>导演引擎</span>
        <select value={run?.engineVersion ?? engineVersion} onChange={(event) => onEngineVersion(event.target.value as DirectorEngine)} disabled={busy || Boolean(run)}>
          <option value="DETERMINISTIC_V1">故事裁定测试 · 不调用角色智能体</option>
          <option value="AGENT_V2">智能体链路诊断 · 全局导演 + 角色演绎</option>
        </select>
      </label>
      {!run ? <button type="button" className="primary-button simulation-start" disabled={busy || !selectedStoryId || !selectedCharacterId || Boolean(story?.completion.errorCount)} onClick={onStart}>{busy ? "正在创建…" : "▷ 开始规则测试"}</button> : null}
      {run ? (
        <div className="simulation-run-actions">
          <button type="button" className="secondary-button" disabled={busy} onClick={onNew}>新建另一场</button>
          <button type="button" className="secondary-button" disabled={busy} onClick={onReset}>重置为新记录</button>
          {!terminal ? <button type="button" className="danger-text-button" disabled={busy} onClick={onStop}>停止本次测试</button> : null}
        </div>
      ) : null}
      <div className="simulation-recent">
        <div><strong>最近记录</strong><span>{runs.length}</span></div>
        {runs.slice(0, 6).map((item) => <RunCard run={item} onOpen={onOpenRun} key={item.id} />)}
        {!runs.length ? <p className="simulation-inline-empty">还没有规则测试记录。</p> : null}
      </div>
    </aside>
  );
}

function ChatMessage({ message, userCharacterName }: { message: SimulationDetail["messages"][number]; userCharacterName: string }): React.ReactNode {
  const [playing, setPlaying] = useState(false);
  const [notice, setNotice] = useState("");
  const user = message.senderType === "USER";
  const narrator = message.senderType === "NARRATOR" || message.senderType === "SYSTEM";
  if (narrator) {
    return <div className="simulation-narrator-message"><span>旁白</span><p>{message.content ?? message.publicCaption}</p></div>;
  }

  function playWhisper(): void {
    if (!message.canPlay || !message.content) {
      setNotice(`${message.senderName}压低了声音，这句话不是说给你的，你没有听清。`);
      return;
    }
    if (!("speechSynthesis" in window)) {
      setNotice("当前浏览器不支持语音播放，请换用最新版浏览器测试。");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(message.content);
    utterance.lang = "zh-CN";
    utterance.rate = 0.94;
    utterance.onstart = () => { setNotice(""); setPlaying(true); };
    utterance.onend = () => setPlaying(false);
    utterance.onerror = () => { setPlaying(false); setNotice("这句悄悄话暂时无法播放，请稍后重试。"); };
    window.speechSynthesis.speak(utterance);
  }

  return (
    <article className={`simulation-chat-message ${user ? "user" : "ai"}${message.visibility === "WHISPER" ? " whisper" : ""}`}>
      <span className="simulation-message-avatar">{message.senderName.slice(0, 1)}</span>
      <div>
        <header><strong>{message.senderName}</strong><span>{user ? `${userCharacterName} · 真人` : "AI 角色"}</span></header>
        {message.visibility === "WHISPER" ? <div className="simulation-whisper-card">
          <p>{message.publicCaption ?? `${message.senderName}说了一句悄悄话`}</p>
          <button type="button" className={`simulation-whisper-bar${message.canPlay ? " playable" : " locked"}${playing ? " playing" : ""}`} onClick={playWhisper} aria-label={message.canPlay ? "播放悄悄话" : "尝试播放悄悄话"}>
            <span className="whisper-play-icon" aria-hidden="true">{playing ? "◼" : "▶"}</span>
            <span className="whisper-wave" aria-hidden="true"><i /><i /><i /><i /><i /><i /></span>
            <strong>{message.durationSeconds ?? 1}″</strong>
            {!message.canPlay ? <span className="whisper-lock" aria-hidden="true">⌁</span> : null}
          </button>
          {notice ? <div className="simulation-whisper-notice" role="status">{notice}</div> : null}
        </div> : <p>{message.content ?? message.publicCaption}</p>}
      </div>
    </article>
  );
}

function SimulationChat({
  run,
  busy,
  onSend,
  onCopy,
}: {
  run: SimulationDetail | null;
  busy: boolean;
  onSend: (content: string) => Promise<boolean>;
  onCopy: () => Promise<void>;
}): React.ReactNode {
  const [content, setContent] = useState("");
  const canSend = run?.status === "WAITING_FOR_USER" && !busy;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!content.trim() || !canSend) return;
    const value = content.trim();
    setContent("");
    if (!(await onSend(value))) setContent(value);
  }

  if (!run) {
    return (
      <main className="simulation-chat empty">
        <span className="simulation-empty-orbit">▷</span>
        <p className="eyebrow">故事规则测试</p>
        <h2>选择角色，进入故事</h2>
        <p>玩家角色只由你输入。系统会安排其他角色、推进故事，并在右侧说明每一步依据。</p>
      </main>
    );
  }

  const terminal = ["COMPLETED", "STOPPED", "FAILED"].includes(run.status);
  return (
    <main className="simulation-chat">
      <header className="simulation-chat-header">
        <div><p className="eyebrow">玩家看到的对话</p><h2>{run.story.title}</h2><span>你扮演 {run.playableCharacter.name} · 草稿 R{run.story.draftRevision}</span></div>
        <div><span className={`simulation-status-pill ${statusClass(run.status)}`}>{STATUS_LABELS[run.status]}</span><button type="button" className="text-button" onClick={() => void onCopy()}>复制记录</button></div>
      </header>
      <div className="simulation-chat-log" aria-live="polite">
        {run.messages.map((message) => <ChatMessage key={message.id} message={message} userCharacterName={run.playableCharacter.name} />)}
        {busy ? <div className="simulation-thinking"><i /><i /><i /><span>正在检查并生成下一轮…</span></div> : null}
      </div>
      {terminal ? <div className={`simulation-terminal ${run.status.toLowerCase()}`}>{run.status === "COMPLETED" ? "本次测试已到达允许的结局。" : run.status === "STOPPED" ? "本次测试已由测试人员停止。" : "本次测试因运行错误结束。"}</div> : null}
      <form className="simulation-composer" onSubmit={submit}>
        <label>
          <span className="sr-only">以 {run.playableCharacter.name} 身份输入文字</span>
          <textarea value={content} onChange={(event) => setContent(event.target.value)} rows={3} maxLength={4000} placeholder={`以 ${run.playableCharacter.name} 身份输入对白、动作或混合表达…`} disabled={!canSend} />
        </label>
        <div><small>Enter 换行 · 本轮只提交一次</small><button className="primary-button compact" disabled={!canSend || !content.trim()}>{busy ? "正在生成回应…" : "发送并推进"}</button></div>
      </form>
    </main>
  );
}

function DecisionMonitor({ decision }: { decision: DirectorDecisionItem | null }): React.ReactNode {
  if (!decision) return <div className="simulation-monitor-empty">发送第一条玩家消息后，这里会显示系统如何理解和推进故事。</div>;
  return (
    <section className="monitor-block director-decision-block">
      <div className="monitor-block-heading"><strong>本轮处理结果</strong><span>{decision.latencyMs < 1_000 ? `${decision.latencyMs} 毫秒` : `${(decision.latencyMs / 1_000).toFixed(1)} 秒`}</span></div>
      <div className="intent-summary"><span>{decision.parsedIntent.kind}</span><p>{decision.parsedIntent.summary}</p></div>
      <dl className="decision-grid">
        <div><dt>下一发言者</dt><dd>{decision.nextSpeakers.map((speaker) => speaker.characterName).join("、") || "系统旁白"}</dd></div>
        <div><dt>等待玩家继续</dt><dd>{decision.waitForUser ? "是" : "否"}</dd></div>
        <div><dt>使用方式</dt><dd>{providerLabel(decision.provider)}</dd></div>
        <div><dt>测试方式</dt><dd>{decision.engineVersion === "AGENT_V2" ? "智能体链路" : "故事裁定器"}</dd></div>
      </dl>
      {decision.approvedPlan ? <div className="intent-summary"><span>{decision.approvedPlan.beatKey ?? "无节拍"}</span><p>{decision.approvedPlan.decisionSummary} · {decision.approvedPlan.performanceBrief}</p></div> : null}
      {decision.plan && decision.approvedPlan ? <dl className="decision-grid">
        <div><dt>导演请求事件</dt><dd>{decision.plan.requestedEventKeys.join("、") || "无"}</dd></div>
        <div><dt>服务器批准事件</dt><dd>{decision.approvedPlan.approvedEventKeys.join("、") || "无"}</dd></div>
        <div><dt>导演请求知识</dt><dd>{decision.plan.requestedKnowledgeKeys.join("、") || "无"}</dd></div>
        <div><dt>服务器批准知识</dt><dd>{decision.approvedPlan.approvedKnowledgeKeys.join("、") || "无"}</dd></div>
      </dl> : null}
      {decision.approvedPlan?.rejectedRequests.length ? <div className="monitor-tags">{decision.approvedPlan.rejectedRequests.map((item) => <span key={item}>{item}</span>)}</div> : null}
      {decision.parsedIntent.signals.length ? <div className="monitor-tags">{decision.parsedIntent.signals.map((signal) => <span key={signal}>{signal}</span>)}</div> : null}
    </section>
  );
}

function IssueMarker({ run, busy, onCreate, onResolve }: { run: SimulationDetail; busy: boolean; onCreate: (input: CreateSimulationIssueInput) => Promise<boolean>; onResolve: (issueId: string) => Promise<boolean> }): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<SimulationIssueType>("PACING_ERROR");
  const [severity, setSeverity] = useState<SimulationIssueSeverity>("WARNING");
  const [note, setNote] = useState("");

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (await onCreate({ messageId: null, type, severity, note })) {
      setNote("");
      setOpen(false);
    }
  }

  return (
    <section className="monitor-block issue-monitor-block">
      <div className="monitor-block-heading"><strong>测试问题</strong><button type="button" className="text-button" onClick={() => setOpen(!open)}>{open ? "取消" : "＋ 标记问题"}</button></div>
      {open ? (
        <form className="simulation-issue-form" onSubmit={submit}>
          <div><label><span>问题类型</span><select value={type} onChange={(event) => setType(event.target.value as SimulationIssueType)}>{Object.entries(ISSUE_TYPE_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label><label><span>严重度</span><select value={severity} onChange={(event) => setSeverity(event.target.value as SimulationIssueSeverity)}>{Object.entries(ISSUE_SEVERITY_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label></div>
          <label><span>问题说明</span><textarea value={note} onChange={(event) => setNote(event.target.value)} rows={3} maxLength={2000} required /></label>
          <button className="secondary-button" disabled={busy || !note.trim()}>保存测试问题</button>
        </form>
      ) : null}
      <div className="simulation-issue-list">
        {run.issues.map((issue) => <article key={issue.id} className={`severity-${issue.severity.toLowerCase()}${issue.status === "RESOLVED" ? " resolved" : ""}`}><span>{issue.status === "RESOLVED" ? "已解决" : ISSUE_SEVERITY_LABELS[issue.severity]}</span><div><strong>{ISSUE_TYPE_LABELS[issue.type]}</strong><p>{issue.note}</p><small>{issue.createdBy?.displayName ?? "系统"} · {formatUpdatedAt(issue.createdAt)}</small>{issue.status === "OPEN" ? <button type="button" className="text-button" disabled={busy} onClick={() => void onResolve(issue.id)}>标记已解决</button> : null}</div></article>)}
        {!run.issues.length ? <p className="simulation-monitor-empty compact">当前没有人工标记的问题。</p> : null}
      </div>
    </section>
  );
}

function SimulationMonitor({ run, busy, onIssue, onResolveIssue }: { run: SimulationDetail | null; busy: boolean; onIssue: (input: CreateSimulationIssueInput) => Promise<boolean>; onResolveIssue: (issueId: string) => Promise<boolean> }): React.ReactNode {
  if (!run) {
    return <aside className="simulation-monitor" aria-label="规则检查面板"><div className="simulation-panel-title"><span>03</span><div><strong>规则检查面板</strong><small>这些内部信息不会显示给玩家</small></div></div><div className="simulation-monitor-placeholder"><span>◎</span><p>开始测试后，可以查看当前场景、故事状态、角色知道的信息和可能结局。</p></div></aside>;
  }
  const decision = run.latestDecision;
  return (
    <aside className="simulation-monitor" aria-label="规则检查面板">
      <div className="simulation-panel-title"><span>03</span><div><strong>规则检查面板</strong><small>检查系统实际记录的故事进展</small></div></div>
      <DecisionMonitor decision={decision} />
      <section className="monitor-block">
        <div className="monitor-block-heading"><strong>当前场景</strong><span>{run.currentScene.key}</span></div>
        <h3>{run.currentScene.title}</h3><p>{run.currentScene.objective}</p>
      </section>
      <section className="monitor-block">
        <div className="monitor-block-heading"><strong>当前故事状态</strong><span>{run.state.length}</span></div>
        <div className="simulation-state-list">{run.state.map((state) => <div className={state.changed ? "changed" : ""} key={state.key}><span><strong>{state.label}</strong><small>{state.key}</small></span><em>{String(state.value)}</em></div>)}</div>
      </section>
      <section className="monitor-block">
        <div className="monitor-block-heading"><strong>当前可能进入的结局</strong><span>{run.endingCandidates.filter((item) => item.eligible).length} 个可进入</span></div>
        <div className="ending-candidate-list">{run.endingCandidates.map((ending) => <div className={ending.eligible ? "eligible" : ""} key={ending.key}><span>{ending.eligible ? "✓" : "○"}</span><div><strong>{ending.title}</strong><small>{ending.reason}</small></div><em>{ending.score}</em></div>)}</div>
      </section>
      <section className="monitor-block">
        <div className="monitor-block-heading"><strong>每个角色目前知道什么</strong><span>{decision?.knowledgeScopes.length ?? 0} 个角色</span></div>
        {decision?.knowledgeScopes.map((scope) => <div className="knowledge-scope-card" key={scope.characterId}><strong>{scope.characterName}</strong><span>{scope.knowledgeKeys.length} 条可以知道 · {scope.protectedKnowledgeKeys.length} 条尚未揭示</span><div>{scope.knowledgeKeys.map((key) => <code key={`available-${key}`}>{key}</code>)}{scope.protectedKnowledgeKeys.map((key) => <code className="protected" key={`protected-${key}`}>尚未揭示 · {key}</code>)}</div></div>)}
        {!decision?.knowledgeScopes.length ? <p className="simulation-monitor-empty compact">还没有本轮角色信息。</p> : null}
      </section>
      <IssueMarker run={run} busy={busy} onCreate={onIssue} onResolve={onResolveIssue} />
    </aside>
  );
}

export function SimulationWorkbench({ initialStoryId = "", initialRunId = "" }: { initialStoryId?: string; initialRunId?: string }): React.ReactNode {
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [runs, setRuns] = useState<SimulationRunListItem[]>([]);
  const [selectedStoryId, setSelectedStoryId] = useState(initialStoryId);
  const [selectedCharacterId, setSelectedCharacterId] = useState("");
  const [engineVersion, setEngineVersion] = useState<DirectorEngine>("AGENT_V2");
  const [story, setStory] = useState<StoryDetail | null>(null);
  const [run, setRun] = useState<SimulationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadRuns = useCallback(async () => {
    const result = await api.simulations({ pageSize: 20 });
    setRuns(result.data);
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.storyLines({ pageSize: 100 }),
      api.simulations({ pageSize: 20 }),
      initialRunId ? api.simulation(initialRunId) : Promise.resolve(null),
    ])
      .then(([storyResult, simulationResult, initialRun]) => {
        setStories(storyResult.data);
        setRuns(simulationResult.data);
        if (initialRun) {
          setRun(initialRun);
          setEngineVersion(initialRun.engineVersion);
          setSelectedStoryId(initialRun.story.id);
          setSelectedCharacterId(initialRun.playableCharacter.id ?? "");
        } else if (!selectedStoryId) {
          setSelectedStoryId(storyResult.data.find((item) => item.status !== "ARCHIVED")?.id ?? "");
        }
      })
      .catch((caught) => setError(errorMessage(caught, "无法读取规则测试页面")))
      .finally(() => setLoading(false));
  }, [initialRunId]);

  useEffect(() => {
    setStory(null);
    setSelectedCharacterId("");
    if (!selectedStoryId || run) return;
    api.storyDetail(selectedStoryId)
      .then((detail) => {
        setStory(detail);
        setSelectedCharacterId(detail.characters.find((character) => character.playable)?.id ?? "");
      })
      .catch((caught) => setError(errorMessage(caught, "无法读取故事线结构")));
  }, [selectedStoryId, run]);

  const selectedStoryTitle = useMemo(() => stories.find((item) => item.id === selectedStoryId)?.title ?? "", [stories, selectedStoryId]);

  async function action<T>(work: () => Promise<T>, message: string, apply: (value: T) => void): Promise<boolean> {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const value = await work();
      apply(value);
      setSuccess(message);
      await loadRuns();
      return true;
    } catch (caught) {
      setError(errorMessage(caught, "规则测试操作失败"));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function start(): void {
    if (!selectedStoryId || !selectedCharacterId) return;
    void action(
      () => api.createSimulation({ storyId: selectedStoryId, playableCharacterId: selectedCharacterId, modelProvider: "CONFIGURED", modelName: "CONFIGURED", engineVersion }),
      "规则测试已创建，正在等待你的第一条消息。",
      setRun,
    );
  }

  function openRun(id: string): void {
    void action(() => api.simulation(id), "已打开测试记录。", (detail) => {
      setRun(detail);
      setEngineVersion(detail.engineVersion);
      setSelectedStoryId(detail.story.id);
      setSelectedCharacterId(detail.playableCharacter.id ?? "");
    });
  }

  async function send(content: string): Promise<boolean> {
    if (!run) return false;
    return action(() => api.sendSimulationMessage(run.id, content, crypto.randomUUID()), "本轮已完整提交。", setRun);
  }

  function reset(): void {
    if (!run) return;
    void action(() => api.resetSimulation(run.id), "已创建一条干净的重置记录。", setRun);
  }

  function stop(): void {
    if (!run) return;
    void action(() => api.stopSimulation(run.id), "本次规则测试已停止。", setRun);
  }

  async function createIssue(input: CreateSimulationIssueInput): Promise<boolean> {
    if (!run) return false;
    return action(() => api.createSimulationIssue(run.id, input), "测试问题已记录。", setRun);
  }

  async function resolveIssue(issueId: string): Promise<boolean> {
    if (!run) return false;
    return action(() => api.resolveSimulationIssue(run.id, issueId), "测试问题已标记为解决。", setRun);
  }

  async function copyRecord(): Promise<void> {
    if (!run) return;
    const text = [`${run.story.title} · ${run.playableCharacter.name} · R${run.story.draftRevision}`, ...run.messages.map((message) => `${message.senderName}：${message.content ?? `${message.publicCaption ?? "悄悄话"}（当前角色无权收听）`}`)].join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setSuccess("测试记录已复制。 ");
    } catch {
      setError("浏览器未允许复制，请手动选择聊天记录。");
    }
  }

  return (
    <div className="simulation-page">
      <div className="simulation-page-heading">
        <div><p className="eyebrow">故事规则检查</p><h1>规则测试</h1><p>用玩家身份检查场景推进、角色知情范围和可进入的结局。这里不是普通玩家试玩。</p></div>
        <div><span>纯文字测试</span><strong>{run ? `${selectedStoryTitle} · ${run.playableCharacter.name}` : "测试模式"}</strong></div>
      </div>
      <Feedback error={error} success={success} />
      {loading ? <div className="simulation-loading"><span /><span /><span /></div> : (
        <div className="simulation-layout">
          <SimulationSetup stories={stories} story={story} selectedStoryId={selectedStoryId} selectedCharacterId={selectedCharacterId} engineVersion={engineVersion} runs={runs} busy={busy} run={run} onStory={setSelectedStoryId} onCharacter={setSelectedCharacterId} onEngineVersion={setEngineVersion} onStart={start} onOpenRun={openRun} onReset={reset} onStop={stop} onNew={() => setRun(null)} />
          <SimulationChat run={run} busy={busy} onSend={send} onCopy={copyRecord} />
          <SimulationMonitor run={run} busy={busy} onIssue={createIssue} onResolveIssue={resolveIssue} />
        </div>
      )}
    </div>
  );
}
